import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";

// Import the API call we created
import { fetchMyEmailNotifications } from "@/api/fetchMyEmailNotifications/fetchMyEmailNotificationsApi";
import {
  FetchMyEmailNotificationsParams,
  FetchMyEmailNotificationsResponse,
  EmailNotification,
} from "@/api/fetchMyEmailNotifications/types";

// Slice state
interface MyEmailNotificationState {
  notifications: EmailNotification[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: MyEmailNotificationState = {
  notifications: [],
  totalCount: 0,
  loading: false,
  error: null,
};

export const myEmailNotificationSlice = createSlice({
  name: "myEmailNotification",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setNotifications(state, action: PayloadAction<EmailNotification[]>) {
      state.notifications = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
  },
});

export default myEmailNotificationSlice.reducer;

export const { setLoading, setError, setNotifications, setTotalCount } =
  myEmailNotificationSlice.actions;

// Thunk: Fetch My Email Notifications
export const fetchMyEmailNotificationsAction =
  (params?: FetchMyEmailNotificationsParams): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const result: FetchMyEmailNotificationsResponse =
        await fetchMyEmailNotifications(params);
      dispatch(setNotifications(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch your email notifications.";
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };
